/**
 * errorWatcher - Monitoreo de errores en runtime
 * Captura y reporta errores de navegacion, fetch y runtime
 */

// Estado del watcher
let isWatching = false;
let errorLog = [];
const MAX_LOG_SIZE = 100;

/**
 * Estructura de un error capturado
 */
const createErrorEntry = (type, error, context = {}) => ({
  id: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  type, // 'fetch', 'runtime', 'navigation', 'unhandled'
  timestamp: new Date().toISOString(),
  route: window.location.pathname,
  message: error?.message || String(error),
  stack: error?.stack?.substring(0, 500) || null,
  context,
});

/**
 * Agrega error al log
 */
const addToLog = entry => {
  errorLog.unshift(entry);
  if (errorLog.length > MAX_LOG_SIZE) {
    errorLog.pop();
  }

  // En desarrollo, mostrar en consola
  if (import.meta.env.DEV) {
    console.group(`[ErrorWatcher] ${entry.type.toUpperCase()}`);
    console.log('Ruta:', entry.route);
    console.log('Mensaje:', entry.message);
    if (entry.context && Object.keys(entry.context).length > 0) {
      console.log('Contexto:', entry.context);
    }
    console.groupEnd();
  }
};

/**
 * Handler para promesas no capturadas
 */
const handleUnhandledRejection = event => {
  const entry = createErrorEntry('unhandled', event.reason, {
    type: 'promise_rejection',
  });
  addToLog(entry);

  // En produccion, mostrar notificacion sutil
  if (!import.meta.env.DEV) {
    showErrorNotification('Ocurrio un error. El equipo ha sido notificado.');
  }
};

/**
 * Handler para errores globales de JavaScript
 */
const handleGlobalError = event => {
  const entry = createErrorEntry(
    'runtime',
    event.error || new Error(event.message),
    {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    }
  );
  addToLog(entry);

  // En produccion, mostrar notificacion sutil
  if (!import.meta.env.DEV) {
    showErrorNotification('Ocurrio un error inesperado.');
  }
};

/**
 * Muestra notificacion de error no intrusiva (solo produccion)
 */
const showErrorNotification = message => {
  // Crear elemento de notificacion si no existe
  let notification = document.getElementById('error-watcher-notification');

  if (!notification) {
    notification = document.createElement('div');
    notification.id = 'error-watcher-notification';
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #f87171;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 9999;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    document.body.appendChild(notification);
  }

  notification.textContent = message;
  notification.style.opacity = '1';

  // Ocultar despues de 5 segundos
  setTimeout(() => {
    notification.style.opacity = '0';
  }, 5000);
};

/**
 * Intercepta fetch global para capturar errores
 */
const interceptFetch = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);

      // Capturar errores HTTP
      if (!response.ok && response.status >= 400) {
        const entry = createErrorEntry(
          'fetch',
          new Error(`HTTP ${response.status}`),
          {
            url: args[0],
            status: response.status,
            statusText: response.statusText,
          }
        );
        addToLog(entry);
      }

      return response;
    } catch (error) {
      const entry = createErrorEntry('fetch', error, {
        url: args[0],
        type: 'network_error',
      });
      addToLog(entry);
      throw error;
    }
  };
};

/**
 * Inicia el error watcher
 */
export const startErrorWatcher = () => {
  if (isWatching) return;

  // Listeners globales
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  window.addEventListener('error', handleGlobalError);

  // Interceptar fetch
  interceptFetch();

  isWatching = true;

  if (import.meta.env.DEV) {
    console.log('[ErrorWatcher] Iniciado - Monitoreando errores...');
  }
};

/**
 * Detiene el error watcher
 */
export const stopErrorWatcher = () => {
  if (!isWatching) return;

  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  window.removeEventListener('error', handleGlobalError);

  isWatching = false;

  if (import.meta.env.DEV) {
    console.log('[ErrorWatcher] Detenido');
  }
};

/**
 * Obtiene el log de errores
 */
export const getErrorLog = () => [...errorLog];

/**
 * Limpia el log de errores
 */
export const clearErrorLog = () => {
  errorLog = [];
};

/**
 * Reporta un error manualmente
 */
export const reportError = (error, context = {}) => {
  const entry = createErrorEntry('manual', error, context);
  addToLog(entry);
  return entry;
};

/**
 * Reporta error de navegacion
 */
export const reportNavigationError = (path, reason) => {
  const entry = createErrorEntry('navigation', new Error(reason), {
    targetPath: path,
    currentPath: window.location.pathname,
  });
  addToLog(entry);
  return entry;
};

/**
 * Obtiene resumen de errores para dashboard
 */
export const getErrorSummary = () => {
  const now = Date.now();
  const oneHourAgo = now - 60 * 60 * 1000;

  const recentErrors = errorLog.filter(
    e => new Date(e.timestamp).getTime() > oneHourAgo
  );

  return {
    total: errorLog.length,
    lastHour: recentErrors.length,
    byType: recentErrors.reduce((acc, e) => {
      acc[e.type] = (acc[e.type] || 0) + 1;
      return acc;
    }, {}),
    lastError: errorLog[0] || null,
  };
};

export default {
  startErrorWatcher,
  stopErrorWatcher,
  getErrorLog,
  clearErrorLog,
  reportError,
  reportNavigationError,
  getErrorSummary,
};
